import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import {
  useUploads,
  type UploadItem,
  type UploadTransport,
} from '@nucleus-commerce/admin-kit/uploads';
import type { Media } from '@nucleus-commerce/api-client/admin';

import { createMediaUploader } from '../api/media-api';

import { mediaKeys } from './media-keys';

export type MediaUploadItem = UploadItem<Media>;

export interface UseMediaUploaderOptions {
  onUploaded?: (media: Media) => void;
  /** Byte transport override; tests pass a fetch-based one because jsdom's XHR cannot send Blobs. */
  transport?: UploadTransport;
}

/**
 * Upload queue bound to the media API. Every confirmed upload lands in the detail cache and
 * invalidates the library; `onUploaded` lets a caller attach the media somewhere.
 */
export function useMediaUploader({ onUploaded, transport }: UseMediaUploaderOptions = {}) {
  const queryClient = useQueryClient();
  const uploader = useMemo(() => createMediaUploader(transport), [transport]);
  const onDone = useCallback(
    (media: Media) => {
      queryClient.setQueryData(mediaKeys.detail(media.id), media);
      void queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
      onUploaded?.(media);
    },
    [queryClient, onUploaded],
  );
  return useUploads(uploader, { concurrency: 3, onDone });
}
